/**
 * Timeframe Selector Component
 * Button group for selecting chart timeframe (Day/Week/Month)
 */

import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { Timeframe } from '../types/pricing.js';

/**
 * Event detail for timeframe change
 */
export interface TimeframeChangeEvent {
  timeframe: Timeframe;
}

@customElement('timeframe-selector')
export class TimeframeSelector extends LitElement {
  // Use Light DOM for consistency with landing page
  protected createRenderRoot() {
    return this;
  }

  @property({ type: String })
  selectedTimeframe: Timeframe = 'd';

  private timeframes: Array<{ value: Timeframe; label: string }> = [
    { value: 'h', label: 'Hour' },
    { value: 'd', label: 'Day' },
    { value: 'w', label: 'Week' },
    { value: 'm', label: 'Month' },
  ];

  /**
   * Handle timeframe selection
   */
  private handleTimeframeClick(timeframe: Timeframe) {
    if (this.selectedTimeframe === timeframe) {
      return; // Already selected
    }

    this.selectedTimeframe = timeframe;

    // Emit custom event
    this.dispatchEvent(
      new CustomEvent<TimeframeChangeEvent>('timeframe-change', {
        detail: { timeframe },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    return html`
      <div class="inline-flex border border-surface-border">
        ${this.timeframes.map(
          ({ value, label }, index) => html`
            <button
              @click="${() => this.handleTimeframeClick(value)}"
              class="${this.selectedTimeframe === value
                ? 'bg-brand-primary text-white'
                : 'bg-surface-elevated text-text-primary hover:bg-surface-overlay'}
              px-4 py-2 text-sm font-medium transition-colors duration-200
              ${index > 0 ? 'border-l border-surface-border' : ''}"
            >
              ${label}
            </button>
          `
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'timeframe-selector': TimeframeSelector;
  }
}
